import { Badge } from '@mantine/core'
import { FunctionComponent } from 'react'
import { Color, Colors, ColorUtil } from '../style/colors'
import { WithClassName } from '../types/utils'
import { OnClickProps } from '../utils/onClickProps'
import { Icon, IconProps } from './Icon'

const getTextColor = (bgColor?: Color) => {
  if (!bgColor) return Colors.Ink
  const ratioDark = ColorUtil.getHexContrastRatio(bgColor, Colors.Ink)
  const ratioLight = ColorUtil.getHexContrastRatio(bgColor, Colors.White)
  return ratioDark > ratioLight ? Colors.Ink : Colors.White
}

export type ChipProps = WithClassName & {
  iconLeft?: IconProps & { clickable?: OnClickProps }
  iconRight?: IconProps & { clickable?: OnClickProps }
  clickable?: OnClickProps
  text: string
  color?: Color
  variant?: 'regular' | 'small'
  style?: React.CSSProperties
  borderColor?: string
}

const ChipIcon = ({ icon, isSmall, side }: { icon: IconProps & { clickable?: OnClickProps }; isSmall: boolean; side: 'left' | 'right' }) => {
  const size = side === 'left' ? (isSmall ? 'sm' : 'md') : isSmall ? 'xs' : 'sm'
  const iconColor = side === 'left' ? icon.fill : (icon.fill ?? Colors.Ink)
  return (
    <span
      className={`inline-flex items-center transition-transform ${icon.clickable ? 'cursor-pointer hover:scale-110 active:scale-95' : ''}`}
      onClick={
        icon.clickable
          ? (e) => {
              e.stopPropagation()
              icon.clickable?.onClick(e)
            }
          : undefined
      }
      data-tracking={icon.clickable?.trackingLabel}
    >
      <Icon size={size} {...icon} color={iconColor} />
    </span>
  )
}

export const Chip: FunctionComponent<ChipProps> = ({ text, iconLeft, iconRight, color, variant, className, clickable, style, borderColor }) => {
  const bg = color ?? Colors.Platinum
  const bd = borderColor ?? Colors.Platinum
  const isSmall = variant === 'small'
  const textColor = getTextColor(bg)

  return (
    <Badge
      component={clickable ? 'button' : 'span'}
      type={clickable ? 'button' : undefined}
      onClick={clickable?.onClick}
      data-tracking={clickable?.trackingLabel}
      bg={bg}
      c={textColor}
      radius="30px"
      leftSection={iconLeft ? <ChipIcon icon={iconLeft} isSmall={isSmall} side="left" /> : undefined}
      rightSection={iconRight ? <ChipIcon icon={iconRight} isSmall={isSmall} side="right" /> : undefined}
      className={className}
      bd={`1px solid ${bd}`}
      style={style}
      data-variant={variant ?? 'regular'}
    >
      {text}
    </Badge>
  )
}

export default Chip
